import { Component, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Role, SetupWizardSection, TextUtils } from '@mira-avant/mtlibs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
	BEFORE_AFTER_STATUS_CHANGED_COLOR,
	BEFORE_AFTER_STATUS_NEW_COLOR,
	BEFORE_AFTER_STATUS_REMOVED_COLOR,
	BEFORE_AFTER_STATUS_UNCHANGED_COLOR,
	BEFORE_AFTER_STATUS_UPSERT_COLOR,
} from '../../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SetupWizard } from '../../../../../../../../services/setup-wizard';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
	selector: 'app-setup-wizard-section-users-status',
	imports: [NzTagModule, NzTooltipModule, NzSpinModule, NzIconModule, NzButtonModule],
	standalone: true,
	templateUrl: './setup-wizard-section-users-status.html',
	styleUrl: './setup-wizard-section-users-status.scss',
})
export class SetupWizardSectionUsersStatus {
	enableEditButton = input<boolean>(true);
	section = input<SetupWizardSection>();
	existingTeachers = signal<number>(0);
	addedTeachers = signal<number>(0);
	removedTeachers = signal<number>(0);
	existingStudents = signal<number>(0);
	addedStudents = signal<number>(0);
	removedStudents = signal<number>(0);
	loading = signal<boolean>(true);
	onEdit = output<SetupWizardSection>();
	private sub?: Subscription;
	public BEFORE_AFTER_STATUS_UPSERT_COLOR = BEFORE_AFTER_STATUS_UPSERT_COLOR;
	public BEFORE_AFTER_STATUS_NEW_COLOR = BEFORE_AFTER_STATUS_NEW_COLOR;
	public BEFORE_AFTER_STATUS_CHANGED_COLOR = BEFORE_AFTER_STATUS_CHANGED_COLOR;
	public BEFORE_AFTER_STATUS_UNCHANGED_COLOR = BEFORE_AFTER_STATUS_UNCHANGED_COLOR;
	public BEFORE_AFTER_STATUS_REMOVED_COLOR = BEFORE_AFTER_STATUS_REMOVED_COLOR;

	constructor(private setupWizard: SetupWizard) {
		const section$ = toObservable(this.section).pipe(distinctUntilChanged());

		this.sub = combineLatest([this.setupWizard.preview$, section$]).subscribe(([preview, section]) => {
			if (!section) {
				this.resetCounts();
				return;
			}

			const sectionBeforeAfter = section.after ?? section.before;

			if (!sectionBeforeAfter?.section_uuid) {
				this.resetCounts();
				return;
			}

			let existingTeachers = 0;
			let addedTeachers = 0;
			let removedTeachers = 0;
			let existingStudents = 0;
			let addedStudents = 0;
			let removedStudents = 0;

			for (const link of preview.payload.usersToSections) {
				const beforeAfter = link.after ?? link.before;

				if (!beforeAfter) {
					throw new Error('Link before/after data is missing');
				}

				if (
					TextUtils.isNonEmptyString(beforeAfter.section_uuid) &&
					beforeAfter.section_uuid !== sectionBeforeAfter.section_uuid
				) {
					continue;
				}

				// Find the user associated with the link in the preview.payload.users
				// area by user_uuid
				const user = preview.payload.users.find(u => {
					const userBeforeAfter = u.after ?? u.before;

					if (!userBeforeAfter) {
						throw new Error('User before/after data is missing');
					}

					// Locate user by matching user-uuid
					return (
						(TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
							userBeforeAfter.user_uuid === beforeAfter.user_uuid) ||
						// or by matching school_id_or_email if it exists
						(TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
							userBeforeAfter.school_id_or_email === beforeAfter.school_id_or_email)
					);
				});

				if (!user) {
					continue;
				}

				const userBeforeAfter = user.after ?? user.before;

				if (!userBeforeAfter) {
					continue;
				}

				if (!link.before && link.after && link.after.enrolled) {
					if (userBeforeAfter.group === Role.TEACHER) {
						addedTeachers++;
					} else if (userBeforeAfter.group === Role.STUDENT) {
						addedStudents++;
					}
				} else if (link.before && link.before.enrolled && !link.after) {
					if (userBeforeAfter.group === Role.TEACHER) {
						existingTeachers++;
					} else if (userBeforeAfter.group === Role.STUDENT) {
						existingStudents++;
					}
				} else if (link.before && link.after && link.before.enrolled && !link.after.enrolled) {
					if (userBeforeAfter.group === Role.TEACHER) {
						removedTeachers++;
					} else if (userBeforeAfter.group === Role.STUDENT) {
						removedStudents++;
					}
				}
			}

			this.existingTeachers.set(existingTeachers);
			this.addedTeachers.set(addedTeachers);
			this.removedTeachers.set(removedTeachers);
			this.existingStudents.set(existingStudents);
			this.addedStudents.set(addedStudents);
			this.removedStudents.set(removedStudents);

			this.loading.set(false);
		});
	}

	editSection(section: SetupWizardSection) {
		this.onEdit.emit(section);
	}

	private resetCounts() {
		this.existingTeachers.set(0);
		this.addedTeachers.set(0);
		this.removedTeachers.set(0);
		this.existingStudents.set(0);
		this.addedStudents.set(0);
		this.removedStudents.set(0);
		this.loading.set(false);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
